import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { findUnit } from '@/lib/content';
import { attemptStore, sessionStore } from '@/lib/storage';
import QuestionCard from '@/components/QuestionCard';
import { durationForMode, isCorrect } from '@/lib/quiz';
import type { InFlightQuiz } from '@/types';

export default function Quiz() {
  const { unitId = '' } = useParams();
  const unit = findUnit(unitId);
  const navigate = useNavigate();
  const [state, setState] = useState<InFlightQuiz | null>(() => sessionStore.get());
  const [now, setNow] = useState(() => Date.now());

  const duration = state ? durationForMode(state.mode) : null;
  const timed = duration !== null;
  const isExam = state?.mode === 'final40';

  useEffect(() => {
    if (state) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [state?.cur]);

  useEffect(() => {
    if (!timed) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [timed]);

  useEffect(() => {
    if (duration === null || !state || !unit) return;
    if (now - state.startedAt < duration) return;
    const finalScore = state.questions.reduce(
      (acc, q, i) => acc + (isCorrect(q, state.answers[i]) ? 1 : 0),
      0,
    );
    attemptStore.append({
      unitId: unit.id,
      mode: state.mode,
      startedAt: state.startedAt,
      completedAt: Date.now(),
      score: finalScore,
      total: state.questions.length,
      questions: state.questions,
      answers: state.answers,
    });
    navigate(`/q/${unit.id}/results`);
  }, [now, duration, state, unit, navigate]);

  if (!unit) return <Navigate to="/" replace />;
  if (!state || state.unitId !== unit.id) return <Navigate to={`/q/${unit.id}`} replace />;

  const { questions, cur, score, answers } = state;
  const total = questions.length;
  const q = questions[cur];

  const remainingMs = duration !== null ? Math.max(0, duration - (now - state.startedAt)) : 0;
  const remainingSec = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(remainingSec / 60);
  const seconds = remainingSec % 60;
  const lowTime = timed && remainingMs <= 5 * 60 * 1000;

  const handleAnswer = (answer: number[], correct: boolean) => {
    const nextAnswers = [...answers];
    nextAnswers[cur] = answer;
    const next: InFlightQuiz = {
      ...state,
      answers: nextAnswers,
      score: correct ? score + 1 : score,
    };
    sessionStore.set(next);
    setState(next);
  };

  const handleNext = () => {
    if (cur >= total - 1) {
      attemptStore.append({
        unitId: unit.id,
        mode: state.mode,
        startedAt: state.startedAt,
        completedAt: Date.now(),
        score: state.score,
        total,
        questions: state.questions,
        answers: state.answers,
      });
      navigate(`/q/${unit.id}/results`);
      return;
    }
    const next: InFlightQuiz = { ...state, cur: cur + 1 };
    sessionStore.set(next);
    setState(next);
  };

  return (
    <>
      <div className="progress-bar">
        {isExam && <div className="exam-banner">Final Exam · tap to choose, then check</div>}
        <div className="progress-header">
          <span className="progress-label">{cur + 1} / {total}</span>
          {timed ? (
            <span className={`fluency-timer ${lowTime ? 'low' : ''}`}>
              {minutes}:{String(seconds).padStart(2, '0')}
            </span>
          ) : (
            <span className="progress-score">{score} correct</span>
          )}
        </div>
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${(cur / total) * 100}%` }} />
        </div>
      </div>
      <QuestionCard
        key={cur}
        question={q}
        alreadyAnswered={answers[cur]}
        onAnswer={handleAnswer}
        onNext={handleNext}
        isLast={cur === total - 1}
        examMode={isExam}
      />
    </>
  );
}
